import { create } from "zustand";
import { getCategories } from "@/store/customContentStore";

const STORAGE_KEY = "hippo.activeCategories";

function defaultActive(): string[] {
  return getCategories().filter((c) => c.enabledByDefault).map((c) => c.id);
}

function loadActive(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const ids = JSON.parse(raw) as string[];
      // Keep only ids that still exist in the taxonomy.
      const valid = ids.filter((id) => getCategories().some((c) => c.id === id));
      if (valid.length) return valid;
    }
  } catch {
    /* ignore corrupt storage */
  }
  return defaultActive();
}

function persist(ids: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

interface CategoryState {
  active: string[];
  isActive: (id: string) => boolean;
  toggle: (id: string) => void;
  setAll: (on: boolean) => void;
  setOnly: (id: string) => void;
  reset: () => void;
}

export const useCategoryStore = create<CategoryState>((set, get) => ({
  active: loadActive(),
  isActive: (id) => get().active.includes(id),
  toggle: (id) => {
    const set_ = new Set(get().active);
    if (set_.has(id)) set_.delete(id);
    else set_.add(id);
    const next = getCategories().filter((c) => set_.has(c.id)).map((c) => c.id);
    persist(next);
    set({ active: next });
  },
  setAll: (on) => {
    const next = on ? getCategories().map((c) => c.id) : [];
    persist(next);
    set({ active: next });
  },
  setOnly: (id) => {
    const next = getCategories().filter((c) => c.id === id).map((c) => c.id);
    persist(next);
    set({ active: next });
  },
  reset: () => {
    const next = defaultActive();
    persist(next);
    set({ active: next });
  },
}));
